import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { LispCode } from "@/components/LispCode";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowsLeftRight, CircleNotch, Copy, Download, CheckCircle, Warning } from "@phosphor-icons/react";

const ALL_PLATFORMS = ["AutoCAD", "BricsCAD", "progeCAD", "AutoCAD Civil 3D", "GstarCAD", "ZWCAD"];
const DEFAULTS = ["AutoCAD", "BricsCAD", "progeCAD"];

const downloadText = (text, name) => {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

export const CrossPlatform = ({ seedCode = "" }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [sel, setSel] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState(null);
  const [genId, setGenId] = useState(null);
  const [projName, setProjName] = useState("");
  const cancelRef = useRef(false);
  const jobRef = useRef(null);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && !code && seedCode) setCode(seedCode);
  };

  const toggle = (p) => setSel((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);

  const run = async () => {
    if (!code.trim()) { toast.error("Paste (or generate) a routine to convert."); return; }
    if (!sel.length) { toast.error("Pick at least one target platform."); return; }
    setBusy(true); setVariants(null); setGenId(null); cancelRef.current = false;
    const t = toast.loading(`Converting to ${sel.length} platform(s)…`);
    try {
      const { job_id } = await api.crossConvert({ code, platforms: sel, version: "latest" });
      jobRef.current = job_id;
      const started = Date.now();
      while (Date.now() - started < 300000) {
        for (let w = 0; w < 2000 && !cancelRef.current; w += 200) { await new Promise((r) => setTimeout(r, 200)); }
        if (cancelRef.current) { toast.message("Conversion cancelled", { id: t }); return; }
        const s = await api.generationStatus(job_id);
        if (s.status === "done") { setVariants(s.result?.variants || []); setGenId(s.result?.id || null); toast.success("Cross-platform variants ready", { id: t }); return; }
        if (s.status === "error") { toast.error(s.error || "Conversion failed", { id: t }); return; }
        if (s.status === "cancelled") { toast.message("Conversion cancelled", { id: t }); return; }
      }
      toast.error("Conversion timed out.", { id: t });
    } catch (err) { toast.error(err?.response?.data?.detail || "Conversion failed", { id: t }); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    cancelRef.current = true; setBusy(false);
    toast.message("Conversion cancelled");
    if (jobRef.current) { try { await api.cancelJob(jobRef.current); } catch (err) { console.debug("cancelJob failed (best-effort)", err); } }
  };

  const saveProject = async () => {
    if (!projName.trim()) { toast.error("Name the project first."); return; }
    if (!genId) { toast.error("Convert a routine first."); return; }
    try { await api.saveProject(projName, genId); setProjName(""); toast.success(`Saved "${projName}" — all variants stored in Projects`); }
    catch { toast.error("Could not save project."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="cross-platform-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <ArrowsLeftRight size={14} weight="bold" /> Cross-Platform
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-3xl rounded-[4px]" data-testid="cross-platform-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><ArrowsLeftRight size={18} weight="bold" className="text-[var(--primary)]" />Cross-Platform Converter — one routine, many CADs</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Convert any AutoLISP routine into side-by-side AutoCAD, BricsCAD and progeCAD variants.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1.5" data-testid="cross-platforms">
          {ALL_PLATFORMS.map((p) => (
            <button key={p} onClick={() => toggle(p)} data-testid={`cross-plat-${p}`}
              className={`px-2 py-1 rounded-[3px] text-[11px] font-mono border transition-colors ${sel.includes(p) ? "border-[var(--primary)] bg-[var(--primary)]/15 text-[var(--primary)]" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}>
              {p}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={run} disabled={busy} data-testid="cross-run-btn"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
              {busy ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsLeftRight size={14} />} Convert
            </Button>
            {busy && (
              <button onClick={cancel} data-testid="cross-cancel-btn"
                className="h-8 px-2.5 rounded-[3px] border border-[var(--alert)]/60 text-[var(--alert)] text-[11px] font-mono hover:bg-[var(--alert)]/10">Cancel</button>
            )}
          </div>
        </div>

        <Textarea value={code} onChange={(e) => setCode(e.target.value)} data-testid="cross-input"
          placeholder="Paste an AutoLISP routine (or open this from a generated result to pre-fill it)…"
          className="min-h-[110px] max-h-[170px] resize-none rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[12px]" />

        {variants && variants.length > 0 && (
          <div className="flex items-center gap-2" data-testid="cross-save-row">
            <Input value={projName} onChange={(e) => setProjName(e.target.value)} placeholder="Save all variants to a project…" data-testid="cross-project-name"
              className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
            <Button size="sm" onClick={saveProject} disabled={!genId} data-testid="cross-save-btn"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--success)]/90 hover:bg-[var(--success)] text-black shrink-0">
              Save to Project
            </Button>
          </div>
        )}

        {variants && variants.length > 0 && (
          <Tabs defaultValue={variants[0].platform || "v0"} className="flex flex-col" data-testid="cross-result">
            <TabsList className="bg-[var(--surface-2)] h-9 p-0 gap-0 rounded-[3px] justify-start">
              {variants.map((v, i) => (
                <TabsTrigger key={i} value={v.platform || `v${i}`} data-testid={`cross-tab-${i}`}
                  className="rounded-none h-9 px-3 font-mono text-xs data-[state=active]:bg-[var(--surface)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--primary)] text-[var(--text-dim)]">
                  {v.platform || `Variant ${i + 1}`}
                </TabsTrigger>
              ))}
            </TabsList>
            {variants.map((v, i) => (
              <TabsContent key={i} value={v.platform || `v${i}`} className="m-0 mt-2 data-[state=inactive]:hidden">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    {v.validation && (
                      <Badge className={`rounded-[2px] text-[10px] font-mono gap-1 px-1.5 border-0 ${v.validation.ok ? "bg-[var(--success)]/15 text-[var(--success)]" : "bg-[var(--alert)]/15 text-[var(--alert)]"}`}>
                        {v.validation.ok ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />}
                        {v.validation.ok ? "Valid" : `${v.validation.errors.length} error(s)`}
                      </Badge>
                    )}
                    <span className="text-[11px] font-mono text-[var(--text-dim)]">{(v.changes || []).length} adaptation(s)</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(v.code); toast.success("Copied"); }} data-testid={`cross-copy-${i}`} className="h-6 rounded-[3px] text-[11px] gap-1"><Copy size={12} />Copy</Button>
                    <Button size="sm" onClick={() => downloadText(v.code, `lispforge_${(v.platform || "cad").toLowerCase().replace(/\s+/g, "")}.lsp`)} data-testid={`cross-download-${i}`} className="h-6 rounded-[3px] text-[11px] gap-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><Download size={12} />.lsp</Button>
                  </div>
                </div>
                {(v.changes || []).length > 0 && (
                  <div className="border border-[var(--border)] rounded-[3px] mb-1.5 max-h-[80px] overflow-auto px-2.5 py-1.5" data-testid={`cross-changes-${i}`}>
                    {v.changes.map((c, ci) => <div key={ci} className="text-[11px] font-mono text-[var(--text-dim)]">• {c}</div>)}
                  </div>
                )}
                <div className="border border-[var(--border)] rounded-[3px]">
                  <ScrollArea className="h-[190px]"><LispCode code={v.code} /></ScrollArea>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
